import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ButtonGroup, DialogWrapper, Drawer } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { Text } from '@reapit/elements/utils/text'
import { FormDetailsFields } from '../../../../components/form-details-fields'
import { FormDetailsValues } from '../../../../components/form-details-fields/schema'
import { useCreateAdminForm } from '../../../../api/forms'
import { useCreateAdminFormVersion } from '../../../../api/form-versions'
import { AdminFormVersionPayload } from '../../../../api/types'

dayjs.extend(customParseFormat)

interface CreateFormDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Create Form drawer. Submits two sequential mutations — create the form, then create its first version
 * under the returned reference — and redirects to the builder for the new form + version. If the version
 * create fails after the form was created, re-submitting only retries the version (no duplicate form).
 */
export const CreateFormDrawer: React.FC<CreateFormDrawerProps> = ({ isOpen, onOpenChange }) => {
  const navigate = useNavigate()
  const formId = useMemo(() => 'create-form-drawer', [])
  const [createdFormRef, setCreatedFormRef] = useState<string | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<FormDetailsValues | null>(null)
  const pendingVersion = useRef<AdminFormVersionPayload | null>(null)

  const [createForm, creatingForm, formData, formSuccess] = useCreateAdminForm()
  const [createVersion, creatingVersion, versionData, versionSuccess] = useCreateAdminFormVersion(createdFormRef)

  const handleClose = () => onOpenChange(false)

  // Reset the chained-create state each time the drawer opens so a fresh form is created every time.
  useEffect(() => {
    if (isOpen) {
      setCreatedFormRef(null)
      setIsConfirmOpen(false)
      setPendingValues(null)
      pendingVersion.current = null
    }
  }, [isOpen])

  // Step 1 complete: capture the new form's reference so the version hook targets the right path.
  useEffect(() => {
    if (!formSuccess || !formData?.referenceID) return
    setCreatedFormRef(formData.referenceID)
  }, [formSuccess, formData])

  // Form reference resolved: create the first version (the version hook now has the correct path).
  useEffect(() => {
    if (!createdFormRef || !pendingVersion.current) return
    createVersion(pendingVersion.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdFormRef])

  // Step 2 complete: redirect to the builder for the new form + version.
  useEffect(() => {
    if (!versionSuccess || !versionData?.referenceID || !createdFormRef) return
    onOpenChange(false)
    navigate(`/forms/${createdFormRef}/versions/${versionData.referenceID}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionSuccess, versionData, createdFormRef])

  const runCreate = (values: FormDetailsValues) => {
    const { name, fullName, formType, region, country, version, validFromUTC, ...flags } = values
    const parsedValidFrom = dayjs(validFromUTC, 'DD/MM/YYYY', true)

    pendingVersion.current = {
      version,
      // Falls through to the raw value when it's not DD/MM/YYYY — covers the unedited case where
      // validFromUTC is already an ISO string from the server, without risking a Date() parse crash.
      validFromUTC: parsedValidFrom.isValid() ? parsedValidFrom.toISOString() : validFromUTC,
      ...flags,
    }

    if (createdFormRef) {
      // Form already created on a previous attempt — only (re)create the version.
      createVersion(pendingVersion.current)
      return
    }

    createForm({
      name,
      fullName: fullName || null,
      formType,
      region: region || 'NONE',
      country,
    })
  }

  // The form's valid submit stashes the values and opens the confirmation modal (rather than creating
  // immediately) — mirroring the main app's confirm-before-write pattern.
  const handleFormValid = (values: FormDetailsValues) => {
    setPendingValues(values)
    setIsConfirmOpen(true)
  }

  const handleConfirmCreate = () => {
    if (!pendingValues) return
    setIsConfirmOpen(false)
    runCreate(pendingValues)
  }

  const isBusy = creatingForm || creatingVersion

  const drawerFooter = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Create
      </Button>
    </ButtonGroup>
  )

  const confirmFooter = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={() => setIsConfirmOpen(false)} type="button">
        Cancel
      </Button>
      <Button
        size="medium"
        variant="primary"
        isBusy={isBusy}
        disabled={isBusy}
        onClick={handleConfirmCreate}
        type="button"
      >
        Create
      </Button>
    </ButtonGroup>
  )

  return (
    <>
      <Drawer isOpen={isOpen} title="Create Form" onClose={handleClose} footerItems={drawerFooter}>
        {isOpen && <FormDetailsFields formId={formId} onSubmit={handleFormValid} />}
      </Drawer>
      <DialogWrapper
        isModalOpen={isConfirmOpen}
        handlerModalClose={() => setIsConfirmOpen(false)}
        title="Create Form"
        footer={confirmFooter}
      >
        <Text font="text-base/regular">Are you sure you wish to create this form?</Text>
      </DialogWrapper>
    </>
  )
}
